import React, { useId } from "react";
import classNames from "classnames";
import type { FormFieldProps } from "./types";
import styles from "./FormField.module.css";

/**
 * FormField component - A wrapper for form controls with label, helper text, and error state.
 * Provides consistent styling and accessibility for form inputs.
 *
 * WCAG AAA Compliant:
 * - Error messages linked to inputs via aria-describedby
 * - aria-invalid set on inputs when error present
 * - Helper text linked via aria-describedby
 * - Required fields marked with aria-required
 * - Error messages announced via role="alert"
 */
export const FormField: React.FC<FormFieldProps> = ({
  label,
  helperText,
  error,
  required = false,
  disabled = false,
  children,
  htmlFor,
  className,
  size = "md",
  "data-testid": testId,
}) => {
  const generatedId = useId();
  const fieldId = htmlFor || `field-${generatedId}`;
  const errorId = `${fieldId}-error`;
  const helperId = `${fieldId}-helper`;

  // Determine which description ID to use
  const descriptionId = error ? errorId : helperText ? helperId : undefined;

  // Clone children to inject accessibility attributes
  const enhancedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement<Record<string, unknown>>(child)) {
      // Only enhance input-like elements
      const elementType = child.type;
      const childProps = child.props;
      const isInputElement =
        elementType === "input" ||
        elementType === "select" ||
        elementType === "textarea" ||
        (typeof elementType === "string" &&
          ["input", "select", "textarea"].includes(elementType));

      if (
        isInputElement ||
        childProps.role === "combobox" ||
        childProps.role === "listbox"
      ) {
        return React.cloneElement(child, {
          "aria-describedby": descriptionId,
          "aria-invalid": error ? true : undefined,
          "aria-required": required ? true : undefined,
          id: childProps.id || fieldId,
        });
      }
    }
    return child;
  });

  return (
    <div
      className={classNames(
        styles.formField,
        {
          [styles.hasError]: !!error,
          [styles.disabled]: disabled,
        },
        className,
      )}
      data-size={size}
      data-testid={testId}
    >
      {label && (
        <label htmlFor={fieldId} className={styles.label}>
          {label}
          {required && (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          )}
          {required && <span className="sr-only">(required)</span>}
        </label>
      )}

      <div className={styles.inputContainer}>{enhancedChildren}</div>

      {error ? (
        <span
          id={errorId}
          className={styles.errorText}
          role="alert"
          aria-live="assertive"
        >
          {error}
        </span>
      ) : helperText ? (
        <span id={helperId} className={styles.helperText}>
          {helperText}
        </span>
      ) : null}
    </div>
  );
};

FormField.displayName = "FormField";

export default FormField;
